// frontend/src/components/marketplace/MarketplaceSearch.tsx
// Search input with autocomplete suggestions

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Clock,
  TrendingUp,
  Package,
  Store,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { useMarketplaceSearch } from '../hooks/useMarketplace';
import { useMarketplaceStore } from '../stores/marketplaceStore';
import { useDebounce } from '@/core/hooks/useDebounce';

interface MarketplaceSearchProps {
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
  showSuggestions?: boolean;
  variant?: 'default' | 'hero' | 'compact';
}

interface SearchSuggestion {
  type: 'product' | 'vendor' | 'category' | 'recent';
  id: string;
  label: string;
  slug: string;
  sublabel?: string;
}

const TRENDING_SEARCHES = [
  'Accessibility',
  'therapy equipment',
  'home modifications',
  'communication devices',
  'adaptive seating',
];

export const MarketplaceSearch: React.FC<MarketplaceSearchProps> = ({
  placeholder = 'Search products, vendors, categories...',
  className = '',
  autoFocus = false,
  onSearch,
  showSuggestions = true,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  const debouncedQuery = useDebounce(query, 300);
  const { data: searchResults, isLoading } = useMarketplaceSearch(
    debouncedQuery,
    { enabled: debouncedQuery.length >= 2 && showSuggestions }
  );
  
  const { recentlyViewedProducts } = useMarketplaceStore();

  // Build suggestions list
  const suggestions: SearchSuggestion[] = React.useMemo(() => {
    const items: SearchSuggestion[] = [];

    if (debouncedQuery.length < 2) {
      // Show recent searches when no query
      if (recentlyViewedProducts.length > 0) {
        items.push(
          ...recentlyViewedProducts.slice(0, 3).map((p) => ({
            type: 'recent' as const,
            id: p.id,
            label: p.name,
            slug: p.slug,
            sublabel: 'Recently viewed',
          }))
        );
      }
      return items;
    }

    if (searchResults) {
      // Add products
      if (searchResults.products?.length > 0) {
        items.push(
          ...searchResults.products.slice(0, 3).map((p) => ({
            type: 'product' as const,
            id: p.id,
            label: p.name,
            slug: p.slug,
            sublabel: `$${p.priceMin?.toFixed(2) || 'Quote'}`,
          }))
        );
      }

      // Add vendors
      if (searchResults.vendors?.length > 0) {
        items.push(
          ...searchResults.vendors.slice(0, 2).map((v) => ({
            type: 'vendor' as const,
            id: v.id,
            label: v.name,
            slug: v.slug,
            sublabel: v.isVerified ? '✓ Verified vendor' : 'Vendor',
          }))
        );
      }
    }

    return items;
  }, [debouncedQuery, searchResults, recentlyViewedProducts]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions || suggestions.length === 0) {
        if (e.key === 'Enter' && query.trim()) {
          handleSearch();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && suggestions[selectedIndex]) {
            handleSuggestionClick(suggestions[selectedIndex]);
          } else if (query.trim()) {
            handleSearch();
          }
          break;
        case 'Escape':
          setIsFocused(false);
          inputRef.current?.blur();
          break;
      }
    },
    [suggestions, selectedIndex, query, showSuggestions]
  );

  const handleSearch = () => {
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      } else {
        navigate(`/marketplace/search?q=${encodeURIComponent(query.trim())}`);
      }
      setIsFocused(false);
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    switch (suggestion.type) {
      case 'product':
      case 'recent':
        navigate(`/marketplace/products/${suggestion.slug}`);
        break;
      case 'vendor':
        navigate(`/marketplace/vendors/${suggestion.slug}`);
        break;
      case 'category':
        navigate(`/marketplace/category/${suggestion.slug}`);
        break;
    }
    setQuery('');
    setIsFocused(false);
  };

  const handleTrendingClick = (term: string) => {
    setQuery(term);
    if (onSearch) {
      onSearch(term);
    } else {
      navigate(`/marketplace/search?q=${encodeURIComponent(term)}`);
    }
    setIsFocused(false);
  };

  const clearQuery = () => {
    setQuery('');
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions]);

  const getSuggestionIcon = (type: SearchSuggestion['type']) => {
    switch (type) {
      case 'product':
        return <Package className="w-4 h-4" />;
      case 'vendor':
        return <Store className="w-4 h-4" />;
      case 'recent':
        return <Clock className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  // Variant styles
  const variantStyles = {
    default: {
      container: 'relative',
      input: 'w-full pl-10 pr-10 py-2.5 bg-cream border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-all',
      icon: 'w-5 h-5',
    },
    hero: {
      container: 'relative',
      input: 'w-full pl-12 pr-12 py-4 bg-white border-2 border-gray-100 rounded-xl shadow-lg focus:ring-4 focus:ring-sage/20 focus:border-sage transition-all text-lg',
      icon: 'w-6 h-6',
    },
    compact: {
      container: 'relative',
      input: 'w-full pl-9 pr-8 py-2 bg-cream border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-all text-sm',
      icon: 'w-4 h-4',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <Search
          className={`absolute left-3 top-1/2 -translate-y-1/2 text-warm-gray ${styles.icon}`}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={styles.input}
        />
        {query && (
          <button
            onClick={clearQuery}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-gray hover:text-navy transition-colors"
          >
            <X className={styles.icon} />
          </button>
        )}
        {isLoading && (
          <Loader2
            className={`absolute right-10 top-1/2 -translate-y-1/2 text-sage animate-spin ${styles.icon}`}
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && isFocused && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
        >
          {suggestions.length > 0 ? (
            <div className="py-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.id}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-cream transition-colors text-left ${
                    index === selectedIndex ? 'bg-cream' : ''
                  }`}
                >
                  <span className="text-warm-gray">
                    {getSuggestionIcon(suggestion.type)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-navy font-medium truncate">
                      {suggestion.label}
                    </p>
                    {suggestion.sublabel && (
                      <p className="text-xs text-warm-gray truncate">
                        {suggestion.sublabel}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-warm-gray flex-shrink-0" />
                </button>
              ))}

              {/* View All Results */}
              {query.trim().length >= 2 && (
                <button
                  onClick={handleSearch}
                  className="w-full px-4 py-3 flex items-center justify-center gap-2 border-t border-gray-100 text-sage hover:bg-cream transition-colors"
                >
                  <Search className="w-4 h-4" />
                  <span className="font-medium">
                    View all results for "{query}"
                  </span>
                </button>
              )}
            </div>
          ) : query.length < 2 ? (
            /* Trending Searches */
            <div className="p-4">
              <div className="flex items-center gap-2 text-warm-gray mb-3">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm font-medium">Trending Searches</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {TRENDING_SEARCHES.map((term) => (
                  <button
                    key={term}
                    onClick={() => handleTrendingClick(term)}
                    className="px-3 py-1.5 bg-cream text-navy text-sm rounded-full hover:bg-sage/10 hover:text-sage transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ) : !isLoading ? (
            /* No Results */
            <div className="p-6 text-center">
              <p className="text-warm-gray">
                No results found for "{query}"
              </p>
              <button
                onClick={handleSearch}
                className="mt-2 text-sage hover:text-sage-dark font-medium"
              >
                Search anyway →
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default MarketplaceSearch;



